import React, { useState } from "react";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  LinearProgress,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import UserAvatar from "../../common/UserAvatar";
import ReplayIcon from "@mui/icons-material/Replay";
import { StyledPaper } from "../../customComponents/styled/styledPaper";
import { StyledTextField } from "../../customComponents/styled/styledInputs";
import {
  StyledTableCell,
  StyledTableRow,
} from "../../customComponents/styled/styledTable";
import {
  useGetGraduatedStudentsQuery,
  useUngraduateStudentsMutation,
} from "./studentApiSlice";
import toast from "../../../helpers/toast";

const PER_PAGE_OPTIONS = [10, 20, 50];

function GraduatedStudentList() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Debounce search
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data, isLoading } = useGetGraduatedStudentsQuery({
    page,
    per_page: perPage,
    q: debouncedSearch,
  });

  const [ungraduate, { isLoading: isUngraduating }] =
    useUngraduateStudentsMutation();

  const students = data?.students || [];
  const pagination = data?.pagination;

  const handleUngraduate = async (student) => {
    try {
      await ungraduate({ student_ids: [student.id] }).unwrap();
      toast.success(
        `${student.first_name} ${student.last_name} moved back to active`
      );
    } catch {
      toast.error("Failed to reverse graduation");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString();
  };

  return (
    <StyledPaper>
      <Box className="paperHeader">
        <Typography variant="formHeading">Graduated Students</Typography>
        <Typography variant="formSubHeading" sx={{ textTransform: "none" }}>
          Students who have been graduated. Use the undo icon to reverse.
        </Typography>
      </Box>

      <Box sx={{ px: 2, pt: 1, pb: 0.5 }}>
        <StyledTextField
          placeholder="Search by name or reg number..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          size="small"
          sx={{ width: { xs: "100%", sm: 280 } }}
        />
      </Box>

      {isLoading && <LinearProgress />}

      {isMobile ? (
        <Box sx={{ p: 2 }}>
          {!isLoading && students.length === 0 && (
            <Typography color="text.secondary" variant="body2" sx={{ textAlign: "center", py: 2 }}>
              No graduated students found.
            </Typography>
          )}
          {students.map((student) => (
            <Box
              key={student.id}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                p: 1.5,
                mb: 1.5,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <UserAvatar
                  src={student.photo}
                  name={`${student.first_name || ""} ${student.last_name || ""}`.trim()}
                  sx={{ width: 36, height: 36, fontSize: "0.9rem" }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ textTransform: "capitalize", color: "primary.main", fontWeight: 600 }}>
                    {student.first_name} {student.last_name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Reg: {student.reg_number || "—"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Last Class: {student.last_classroom?.name || "—"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Graduated: {formatDate(student.graduated_at)}
                  </Typography>
                </Box>
              </Box>

              <Button
                variant="outlined"
                startIcon={<ReplayIcon />}
                sx={{ mt: 1.5, textTransform: "none" }}
                disabled={isUngraduating}
                onClick={() => handleUngraduate(student)}
              >
                Undo Graduation
              </Button>
            </Box>
          ))}
        </Box>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <StyledTableCell>Photo</StyledTableCell>
                <StyledTableCell>Name</StyledTableCell>
                <StyledTableCell>Reg Number</StyledTableCell>
                <StyledTableCell>Gender</StyledTableCell>
                <StyledTableCell>Last Class</StyledTableCell>
                <StyledTableCell>Graduated On</StyledTableCell>
                <StyledTableCell>Undo</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!isLoading && students.length === 0 && (
                <StyledTableRow>
                  <StyledTableCell colSpan={7} align="center">
                    No graduated students found.
                  </StyledTableCell>
                </StyledTableRow>
              )}
              {students.map((student) => (
                <StyledTableRow key={student.id}>
                  <StyledTableCell>
                    <UserAvatar
                      src={student.photo}
                      name={`${student.first_name || ""} ${student.last_name || ""}`.trim()}
                      sx={{ width: 28, height: 28, fontSize: "0.7rem" }}
                    />
                  </StyledTableCell>
                  <StyledTableCell>
                    {student.first_name} {student.last_name}
                  </StyledTableCell>
                  <StyledTableCell>{student.reg_number || "—"}</StyledTableCell>
                  <StyledTableCell>{student.gender}</StyledTableCell>
                  <StyledTableCell>{student.last_classroom?.name || "—"}</StyledTableCell>
                  <StyledTableCell>
                    {formatDate(student.graduated_at)}
                  </StyledTableCell>
                  <StyledTableCell>
                    <ReplayIcon
                      titleAccess="Undo graduation"
                      className="tableCellLink"
                      sx={{ color: "primary.safeOnLight", opacity: isUngraduating ? 0.4 : 1 }}
                      onClick={() => !isUngraduating && handleUngraduate(student)}
                    />
                  </StyledTableCell>
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <TablePagination
        rowsPerPageOptions={PER_PAGE_OPTIONS}
        component="div"
        count={pagination?.total_items ?? 0}
        rowsPerPage={perPage}
        page={page - 1}
        onPageChange={(_, newPage) => setPage(newPage + 1)}
        onRowsPerPageChange={(e) => {
          setPerPage(parseInt(e.target.value, 10));
          setPage(1);
        }}
      />
    </StyledPaper>
  );
}

export default GraduatedStudentList;
